import { css, html } from "lit";
import { ifDefined } from "lit-html/directives/if-defined.js";
import "../../atom/Icon/index.js";
import "../Button/index.js";
import "../Link/index.js";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { rif } from "../../../helpers/renderIf.js";
import { getWrapperClasses, getMessageClasses } from "./utils.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let DsfFileCard = class extends BaseComponent {
  constructor() {
    super(...arguments);
    this.dsf_file_size = 0;
    this.dsf_locale = navigator.language;
    this.dsf_error = false;
  }
  get isUploading() {
    return this.dsf_upload_progress !== void 0;
  }
  get isIndeterminate() {
    return this.dsf_upload_progress === "indeterminate";
  }
  get uploadProgress() {
    return this.isIndeterminate || !this.dsf_upload_progress ? 50 : Number(this.dsf_upload_progress);
  }
  get timestampFormatted() {
    const rawDate = this.dsf_timestamp;
    const parsedDate = rawDate instanceof Date ? rawDate : typeof rawDate === "string" ? new Date(rawDate) : void 0;
    return parsedDate ? Intl.DateTimeFormat(this.dsf_locale, { dateStyle: "short", timeStyle: "short" }).format(
      parsedDate
    ) : "";
  }
  get fileSizeInMbFormatted() {
    return `(${(this.dsf_file_size / (1024 * 1024)).toFixed(2)} MB)`;
  }
  handleLinkClick() {
    this.emit("file-click");
  }
  handleDeleteClick() {
    this.emit("delete-click");
  }
  render() {
    this.style.setProperty("--dsf-upload-progress", `${this.uploadProgress}%`);
    const fileName = html`<div class="flex items-baseline gap-8">
      ${!this.dsf_error ? html`<dsf-link
            class="w-fit"
            dsf_size="medium"
            ?disabled=${this.isUploading}
            dsf_href=${ifDefined(this.dsf_file_url)}
            @click=${this.handleLinkClick}
            >${this.dsf_file_name}</dsf-link
          >` : html`<span class="dsf-text-xs text-neutral-700">${this.dsf_file_name}</span>`}
      ${rif(
      this.dsf_file_size,
      html`<span class="dsf-text-xxs text-neutral-300">${this.fileSizeInMbFormatted}</span>`
    )}
    </div>`;
    return html`<div class=${getWrapperClasses(this)}>
      ${rif(
      this.dsf_icon,
      html`<dsf-icon
          dsf_name=${ifDefined(this.dsf_icon)}
          class="h-14 w-12 text-neutral-300"
        ></dsf-icon>`
    )}
      <div class="flex flex-col gap-4">
        ${fileName}
        ${rif(
      this.dsf_message,
      html`<div class="text-nowrap flex items-center gap-4">
            <span class=${getMessageClasses(this)}>${this.dsf_message}</span>
            <span class="dsf-text-xxs text-neutral-300">${this.timestampFormatted}</span>
          </div>`
    )}
      </div>
      <div class="ml-auto">
        ${rif(
      this.isUploading,
      html`<div class=${`progress ${this.isIndeterminate ? "animate-spin" : ""}`}></div>`
    )}
        ${rif(
      !this.isUploading,
      html`<dsf-button
            dsf_square
            dsf_level="secondary"
            dsf_icon_left="Trash2"
            dsf_size="small"
            dsf_accessibility_label=${ifDefined(this.dsf_delete_accessibility_label)}
            @click=${this.handleDeleteClick}
          >
          </dsf-button>`
    )}
      </div>
    </div>`;
  }
};
DsfFileCard.shadowRootOptions = {
  ...BaseComponent.shadowRootOptions,
  delegatesFocus: false
};
DsfFileCard.styles = [
  ...BaseComponent.styles,
  css`
      .progress {
        width: calc(1.8 * var(--dsf-base-size));
        height: calc(1.8 * var(--dsf-base-size));
        border-radius: 50%;
        background: radial-gradient(closest-side, white calc(64% - 1px), transparent 64% 100%),
          conic-gradient(
            rgb(var(--dsf-color-secondary-300)) var(--dsf-upload-progress),
            rgb(var(--dsf-color-neutral-100)) 0
          );
      }
    `
];
__decorateClass([
  prop({ type: String })
], DsfFileCard.prototype, "dsf_file_name", 2);
__decorateClass([
  prop({ type: String })
], DsfFileCard.prototype, "dsf_file_url", 2);
__decorateClass([
  prop({ type: Number })
], DsfFileCard.prototype, "dsf_file_size", 2);
__decorateClass([
  prop({ type: String })
], DsfFileCard.prototype, "dsf_message", 2);
__decorateClass([
  prop({ type: String })
], DsfFileCard.prototype, "dsf_timestamp", 2);
__decorateClass([
  prop({ type: String })
], DsfFileCard.prototype, "dsf_locale", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfFileCard.prototype, "dsf_error", 2);
__decorateClass([
  prop({ type: String })
], DsfFileCard.prototype, "dsf_upload_progress", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfFileCard.prototype, "dsf_icon", 2);
__decorateClass([
  prop({ type: String })
], DsfFileCard.prototype, "dsf_delete_accessibility_label", 2);
DsfFileCard = __decorateClass([
  defineElement("dsf-file-card")
], DsfFileCard);
export {
  DsfFileCard
};
